import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef, useSyncExternalStore } from 'react';
import { notifyStorageKeyChange, subscribeToStorageKey } from '@/utils/storage';

// Generic JSON-backed localStorage state, implemented the same way as
// useLocalStorageBoolean: a tiny external store instead of a `useEffect` +
// `setState` hydration pair (react-hooks/set-state-in-effect).
// `useSyncExternalStore` returns `defaultValue` on the server and on the
// first client render (matching SSR), then the real stored value once
// mounted. `setValue` writes straight to localStorage and notifies
// subscribers.
//
// `enabled` gates both the read and the write: callers that render this
// state in more than one mode (e.g. an inline vs. popover variant) can
// disable persistence for the instance that must not read or clobber the
// shared key.

export function useJsonLocalStorage(storageKey, parse, defaultValue, enabled) {
  const $ = _c(13);
  const defaultRef = useRef(defaultValue);
  const cacheRef = useRef(null);
  let t0;
  if ($[0] !== storageKey) {
    t0 = onChange => subscribeToStorageKey(storageKey, onChange);
    $[0] = storageKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== enabled || $[3] !== parse || $[4] !== storageKey) {
    t1 = () => {
      if (!enabled) {
        return defaultRef.current;
      }
      const raw = localStorage.getItem(storageKey);
      const cached = cacheRef.current;
      if (cached && cached.raw === raw) {
        return cached.value;
      }
      const parsed = parse(raw) ?? defaultRef.current;
      cacheRef.current = {
        raw,
        value: parsed
      };
      return parsed;
    };
    $[2] = enabled;
    $[3] = parse;
    $[4] = storageKey;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => defaultRef.current;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const value = useSyncExternalStore(t0, t1, t2);
  let t3;
  if ($[7] !== enabled || $[8] !== storageKey) {
    t3 = next => {
      if (!enabled) {
        return;
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {}
      notifyStorageKeyChange(storageKey);
    };
    $[7] = enabled;
    $[8] = storageKey;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const setValue = t3;
  let t4;
  if ($[10] !== setValue || $[11] !== value) {
    t4 = [value, setValue];
    $[10] = setValue;
    $[11] = value;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}